import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchEvents, fetchCampeonatos, urlDeCampeonato, type Evento, type Campeonato } from '@/lib/api';
import { useSession } from '@/store/SessionContext';
import { LegalNotice } from '@/components/LegalNotice';

/** Desplegable de competiciones de rfealive.es (llama a la Cloud Function
 *  `campeonatos`). */
const DESPLEGABLE_CAMPEONATOS = true;

/** Normaliza para búsqueda insensible a mayúsculas y acentos. */
function norm(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/** ¿La flag es un destacado "de verdad" (récord de España, líder nacional o
 *  mínima internacional)? Mismo criterio que la caja Destacados de la
 *  herramienta local (podios-stories/app.py, anotar_destacados). PB/SB en
 *  solitario NO cuentan: son logros personales, no de alcance nacional. */
function esFlagDestacada(f: string | [string, string]): boolean {
  return Array.isArray(f)
    ? f[0] === 'MINIMA' || f[0] === 'MINIMA_REPESCA'
    : f === 'RECORD_ESPANA' || f === 'N1_ABSOLUTO' || f === 'N1_CATEGORIA';
}

function tieneDestacado(e: Evento): boolean {
  return e.podio.some((p) => (p.flags ?? []).some(esFlagDestacada));
}

/** Clase de color de una etiqueta del cruce — SOLO en la web (la imagen y
 *  el vídeo generados no usan esto). Código acordado con Riki 2026-07-08:
 *  R.Esp rojo intenso · LE rojo claro · Mín. de categoría (con sufijo
 *  U18/U20/U23) morado claro · Mín. absoluta morado intenso · PB/SB verde
 *  por defecto. */
function pillClass(tag: string): string {
  if (tag.includes('all-time')) return 'ld-pill ld-pill--hist'; // top-10 histórico: dorado
  if (tag.startsWith('R.Esp')) return 'ld-pill ld-pill--record';
  if (tag.startsWith('LE')) return 'ld-pill ld-pill--lider';
  if (tag.startsWith('Mín')) return /U\d/.test(tag) ? 'ld-pill ld-pill--mincat' : 'ld-pill ld-pill--minabs';
  return 'ld-pill';
}

/** id DOM estable de la tarjeta de una prueba, para el salto desde la caja
 *  Destacados (pedido de Riki 2026-07-08). */
function evDomId(e: Evento): string {
  return 'ev-' + norm(`${e.pruebaId ?? e.nombre}|${e.ronda}|${e.hora}|${e.fecha}`).replace(/[^a-z0-9|]+/g, '-');
}

/** Clave de orden por fecha+hora (mayor = más reciente). Tolerante a
 *  formatos yyyy-mm-dd y dd/mm/yyyy; sin fecha → 0 (conserva el orden). */
function tsClave(e: Evento): number {
  const f = (e.fecha || '').trim();
  let y = 0, mo = 0, d = 0;
  let m = f.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) { y = +m[1]; mo = +m[2]; d = +m[3]; }
  else {
    m = f.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (m) { d = +m[1]; mo = +m[2]; y = +m[3]; }
  }
  const hm = (e.hora || '').match(/(\d{1,2}):(\d{2})/);
  const hh = hm ? +hm[1] : 0;
  const mi = hm ? +hm[2] : 0;
  return ((y * 12 + mo) * 31 + d) * 1440 + hh * 60 + mi;
}

export function Home() {
  const navigate = useNavigate();
  const { url, setUrl, data, setData, loadedUrl, setLoadedUrl, seleccion, setSeleccion, setFotoDataUrl } = useSession();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [campeonatos, setCampeonatos] = useState<Campeonato[] | null>(null);
  const [query, setQuery] = useState('');

  // Carga la lista de competiciones de rfealive.es para el desplegable.
  useEffect(() => {
    if (!DESPLEGABLE_CAMPEONATOS) {
      setCampeonatos([]);
      return;
    }
    let cancel = false;
    fetchCampeonatos().then((c) => { if (!cancel) setCampeonatos(c); });
    return () => { cancel = true; };
  }, []);

  // Carga inicial (manual): limpia el listado.
  async function cargar(u: string) {
    const target = u.trim();
    if (!target) return;
    setError(null);
    setLoading(true);
    setData(null);
    try {
      const r = await fetchEvents(target);
      setData(r);
      setLoadedUrl(target);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  // Refresco manual: re-scrapea la URL ya cargada sin vaciar el listado.
  const refresh = useCallback(async () => {
    if (!loadedUrl) return;
    setRefreshing(true);
    setError(null);
    try {
      const r = await fetchEvents(loadedUrl);
      setData(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRefreshing(false);
    }
  }, [loadedUrl, setData]);

  function onScrape(e: React.FormEvent) {
    e.preventDefault();
    void cargar(url);
  }

  function onPickCampeonato(code: string) {
    if (!code) return;
    const u = urlDeCampeonato(code);
    setUrl(u);
    void cargar(u);
  }

  // Tokeniza la búsqueda: cada palabra debe aparecer en el nombre de la prueba
  // (AND), insensible a orden y espaciado. Así «martillo m70» o «m70 martillo»
  // filtran igual y «200» no cuela un «4x400».
  const tokens = norm(query).split(/\s+/).filter(Boolean);
  const filtra = (evs: Evento[]) =>
    tokens.length ? evs.filter((e) => { const n = norm(e.nombre); return tokens.every((t) => n.includes(t)); }) : evs;
  // Pruebas ordenadas de más reciente a más antigua (RFEA trae fecha+hora) y
  // SOLO las terminadas (con podio/resultados). Las que faltan por disputarse
  // (sin resultados) se ocultan; el estado de la fuente no es fiable.
  // La fuente puede traer la misma prueba repetida: deduplicamos por
  // nombre+ronda+hora+fecha para no pintar filas idénticas.
  const eventos = data
    ? (() => {
        const vistos = new Set<string>();
        const out: Evento[] = [];
        for (const e of data.eventos) {
          const sig = `${norm(e.nombre)}|${e.ronda}|${e.hora}|${e.fecha}`;
          if (vistos.has(sig)) continue;
          vistos.add(sig);
          out.push(e);
        }
        return out.sort((a, b) => tsClave(b) - tsClave(a));
      })()
    : [];
  const terminadas = eventos.filter((e) => e.podio.length > 0);
  const visibles = filtra(terminadas);
  // Destacados de la competición (récord/líder/mínima) sobre TODAS las
  // pruebas terminadas (ignora el filtro de búsqueda, igual que la caja de
  // la herramienta local). Pedido de Riki 2026-07-08: verlos arriba y poder
  // saltar directamente a cada resultado.
  const destacados = terminadas.flatMap((e) =>
    e.podio.filter((p) => (p.flags ?? []).some(esFlagDestacada)).map((p) => ({ ev: e, p })),
  );
  // Las pruebas con destacado suben arriba del todo; el resto conserva el
  // orden por fecha/hora de siempre (sort es estable).
  const visiblesOrdenadas = [...visibles].sort(
    (a, b) => Number(tieneDestacado(b)) - Number(tieneDestacado(a)),
  );

  /** Salta a la tarjeta de la prueba y la resalta. Si el filtro la oculta,
   *  lo limpia primero y espera al repintado. */
  function irAPrueba(e: Evento) {
    if (query) setQuery('');
    window.setTimeout(() => {
      const el = document.getElementById(evDomId(e));
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.remove('ld-flash');
      void el.offsetWidth; // reinicia la animación si se clica dos veces
      el.classList.add('ld-flash');
    }, 60);
  }

  return (
    <div className="ld-stack ld-stack--lg">
      <div className="ld-card">
        <h2 className="ld-card__title">Pegar URL de competición</h2>
        <p className="ld-card__hint">
          Conersys, FAM (atletismomadrid), RFEA Live (.me / .es). Se detecta la fuente automáticamente.
        </p>
        <form className="ld-row" onSubmit={onScrape}>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://conersyslive.es/..."
            style={{ flex: 1 }}
            required
          />
          <button type="submit" className="ld-btn ld-btn--primary" disabled={loading || !url.trim()}>
            {loading ? 'Cargando…' : 'Cargar'}
          </button>
        </form>

        <div className="ld-row" style={{ marginTop: 12 }}>
          <span className="muted mono" style={{ fontSize: 11, letterSpacing: '0.06em' }}>
            O ELIGE DE RFEA LIVE
          </span>
          <select
            value=""
            disabled={loading || campeonatos === null}
            onChange={(e) => onPickCampeonato(e.target.value)}
            style={{ flex: 1, minWidth: 220 }}
          >
            <option value="">
              {campeonatos === null
                ? 'Cargando competiciones de la RFEA, danos unos segundos…'
                : campeonatos.length
                  ? 'Selecciona una competición…'
                  : '(no se pudo cargar la lista)'}
            </option>
            {(campeonatos ?? []).map((c) => (
              <option key={c.code} value={c.code}>
                {c.titulo} · {c.fecha_inicio}
              </option>
            ))}
          </select>
        </div>

        {campeonatos === null ? (
          <p className="muted" style={{ display: 'flex', alignItems: 'center', marginTop: 12, fontSize: 13 }}>
            <span className="ld-spinner" aria-hidden />
            Cargando competiciones de la RFEA, danos unos segundos…
          </p>
        ) : null}

        {error ? (
          <div className="ld-login__error" style={{ marginTop: 12 }}>
            {error}
          </div>
        ) : null}
      </div>

      {data ? (
        <>
          <div className="ld-card">
            <div className="ld-spread">
              <div>
                <p className="muted mono" style={{ margin: 0, fontSize: 11, letterSpacing: '0.06em' }}>
                  {data.fuente.toUpperCase()} · {data.fecha ?? '—'}
                </p>
                <h2 className="ld-card__title" style={{ margin: '4px 0 0' }}>
                  {data.competicion}
                </h2>
                {data.sede ? <p className="muted" style={{ margin: '4px 0 0' }}>{data.sede}</p> : null}
              </div>
              <div className="ld-row" style={{ alignItems: 'center', gap: 12 }}>
                <span className="ld-pill ld-pill--muted">{terminadas.length} con resultados</span>
                <button
                  type="button"
                  className="ld-btn ld-btn--ghost ld-btn--small"
                  onClick={() => void refresh()}
                  disabled={refreshing}
                  title="Actualizar ahora"
                >
                  {refreshing ? (
                    <>
                      <span className="ld-spinner" aria-hidden />
                      Actualizando…
                    </>
                  ) : (
                    <>↻ Actualizar</>
                  )}
                </button>
              </div>
            </div>

            <div className="ld-row" style={{ marginTop: 16 }}>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar prueba… (p. ej. Martillo M70, 100, salto)"
                style={{ flex: 1 }}
                aria-label="Buscar prueba"
              />
              {query ? (
                <button type="button" className="ld-btn ld-btn--ghost ld-btn--small" onClick={() => setQuery('')}>
                  Limpiar
                </button>
              ) : null}
            </div>
          </div>

          {destacados.length ? (
            <div className="ld-card" style={{ borderColor: 'var(--ld-accent-border)' }}>
              <h3 style={{ margin: '0 0 10px', fontSize: 14, color: 'var(--ld-accent)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Destacados ({destacados.length})
              </h3>
              {/* Rejilla de tarjetas compactas (pedido de Riki 2026-07-08: antes
                  cada destacado era una línea a todo lo ancho; ahora se apilan
                  en horizontal, ~3-4 por fila en escritorio, y el grid iguala
                  los anchos para que los tamaños sean estables). */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                {destacados.map(({ ev, p }, i) => (
                  <button
                    type="button"
                    key={`${evDomId(ev)}__${p.puesto}__${i}`}
                    className="ld-dest__item"
                    onClick={() => irAPrueba(ev)}
                    title="Ir al resultado"
                  >
                    <span style={{ display: 'flex', justifyContent: 'space-between', gap: 6, width: '100%' }}>
                      <span style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.25 }}>{p.nombre}</span>
                      <span className="faint mono" style={{ fontSize: 11, flexShrink: 0 }}>↓</span>
                    </span>
                    <span className="muted" style={{ fontSize: 11 }}>
                      {ev.nombre} · <span className="mono">{p.marca}</span>
                    </span>
                    {p.etiquetas?.length ? (
                      <span className="ld-podium__pills" style={{ marginTop: 2 }}>
                        {(p.etiquetas ?? []).map((tag) => (
                          <span key={tag} className={pillClass(tag)}>{tag}</span>
                        ))}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="ld-card">
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                No hay ninguna marca destacada (récord de España, mínima internacional o líder nacional) en esta competición.
              </p>
            </div>
          )}

          {(() => {
            const goEditor = (ev: Evento) => {
              const key = ev.pruebaId ?? ev.nombre;
              const prevKey = seleccion ? (seleccion.evento.pruebaId ?? seleccion.evento.nombre) : null;
              // Cambiar de prueba descarta la foto de la anterior (no aplica
              // a "volver al editor", que no pasa por aquí).
              if (key !== prevKey) setFotoDataUrl(undefined);
              setSeleccion({ evento: ev, competicion: data.competicion, fecha: data.fecha ?? '' });
              navigate('/editor');
            };
            return (
              <>
                <Section title="Resultados" events={visiblesOrdenadas} onPick={goEditor} />
                {!terminadas.length ? (
                  <p className="faint mono" style={{ margin: 0, fontSize: 12 }}>
                    Aún no hay pruebas con resultados. Pulsa ↻ Actualizar cuando haya finales.
                  </p>
                ) : tokens.length && !visibles.length ? (
                  <p className="faint mono" style={{ margin: 0, fontSize: 12 }}>
                    Ninguna prueba coincide con «{query.trim()}».
                  </p>
                ) : null}
              </>
            );
          })()}
        </>
      ) : null}

      <LegalNotice />
    </div>
  );
}

function Section({
  title,
  events,
  onPick,
}: {
  title: string;
  events: Evento[];
  onPick: (ev: Evento) => void;
}) {
  if (!events.length) return null;
  return (
    <div>
      <h3 style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--ld-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {title}
      </h3>
      <div className="ld-stack">
        {events.map((ev, i) => (
          <article key={`${ev.pruebaId ?? ev.nombre}__${ev.ronda}__${ev.hora}__${i}`} id={evDomId(ev)} className="ld-event">
            <div className="ld-event__head">
              <span className="ld-event__name">{ev.nombre}</span>
              <span className="ld-event__meta">
                {ev.ronda || '—'} · {ev.hora || '—'}
                {ev.viento ? ` · viento ${ev.viento}` : ''}
              </span>
              {ev.podio.length ? (
                <button
                  type="button"
                  className="ld-btn ld-btn--ghost ld-btn--small"
                  style={{ marginLeft: 'auto' }}
                  onClick={() => onPick(ev)}
                >
                  Generar →
                </button>
              ) : null}
            </div>
            {ev.podio.length ? (
              <div className="ld-podium">
                {ev.podio.map((p) => (
                  <div key={p.puesto + p.nombre} className="ld-podium__pos">
                    <div className="ld-podium__num">#{p.puesto}</div>
                    <div className="ld-podium__athlete">{p.nombre}</div>
                    <div className="ld-podium__club">{p.club_real || p.club}</div>
                    <div className="ld-podium__mark">
                      {p.marca}
                      {p.viento ? <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>{p.viento}</span> : null}
                    </div>
                    {p.etiquetas?.length ? (
                      <div className="ld-podium__pills">
                        {(p.etiquetas ?? []).map((tag) => (
                          <span key={tag} className={pillClass(tag)}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <p className="faint mono" style={{ margin: 0, fontSize: 12 }}>
                Sin podio aún.
              </p>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}
